import { GridProps } from '@sm-dashboard/types';
import { formatDate } from '@sm-dashboard/formatting';
import { Box, Chip, Link, Tooltip } from '@mui/material';
import { TooltipCell } from './TooltipCell';
import { ViewRichText } from '@sm-dashboard/form-provider';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CrisisAlertOutlinedIcon from '@mui/icons-material/CrisisAlertOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import { TooltipComponent } from './TooltipComponent';

export type RenderCellOptions = { preferMobileOverride?: boolean };

export const renderCell = <T, U>(
  row: T | U,
  column: GridProps<T>['columns'][number],
  maxWidth?: number,
  options?: RenderCellOptions
) => {
  if (options?.preferMobileOverride && column.mobileRenderCell) {
    return column.mobileRenderCell(row);
  }
  if (column.renderCell) {
    return column.renderCell(row);
  }

  let value: any;

  if (column.valueGetter) {
    value = column.valueGetter(row);
  } else {
    value = row[column.field as keyof typeof row];
  }

  if (typeof value === 'object') {
    try {
      value = JSON.stringify(value);
    } catch (error) {
      console.log('error', error);
    }
  }

  if (Array.isArray(value)) {
    value = value.join(', ');
  }

  if (value === null || value === undefined || value === 'null' || value === 'undefined') {
    value = '';
  }

  const stringValue = value ? String(value) : '';

  const lineClamp = column?.displayLevel === 'expanded' ? 5 : column?.displayLevel === 'standard' ? 3 : 1;

  switch (column.type) {
    case 'text':
      return <TooltipCell value={stringValue} lineClamp={lineClamp} />;
    case 'richText':
      return stringValue ? <ViewRichText value={stringValue} maxHeight={100} /> : '';
    case 'date':
    case 'dateTime':
      return stringValue ? formatDate(stringValue, column.type === 'date') : '';
    case 'amount':
      return stringValue ? (
        <Chip
          label={`${value} ₪`}
          size="small"
          sx={{
            backgroundColor: column?.chipColor || undefined,
            color: 'primary.main'
          }}
        />
      ) : (
        ''
      );
    case 'chip':
      return stringValue ? (
        <Chip
          label={stringValue}
          size="small"
          sx={{
            ...(column?.chipColor
              ? { backgroundColor: theme => theme.palette[column.chipColor ? column.chipColor : 'primary'].main }
              : {}),
            color: column?.chipColor ? theme => theme.palette.common.white : theme => theme.palette.primary.main
          }}
        />
      ) : (
        ''
      );
    case 'phone':
      return stringValue ? (
        <TooltipComponent tooltipTitle={stringValue}>
          <Link href={`tel: ${stringValue}`} variant="subtitle2">
            {stringValue}
          </Link>
        </TooltipComponent>
      ) : (
        ''
      );
    case 'email':
      return stringValue ? (
        <TooltipComponent tooltipTitle={stringValue}>
          <Link href={`mailto: ${stringValue}`} variant="subtitle2">
            {stringValue}
          </Link>
        </TooltipComponent>
      ) : (
        ''
      );
    case 'doc':
      return stringValue ? (
        <Tooltip title="פתח מסמך">
          <Link target="_blank" href={stringValue} variant="subtitle2">
            פתח מסמך
          </Link>
        </Tooltip>
      ) : (
        ''
      );
    case 'sourceType': {
      const resolvedType = sourceType[stringValue] ? stringValue : 'organic';
      const bgcolor = sourceType[resolvedType]?.bgcolor ?? 'info.main';
      return (
        <Tooltip title={`מקור: ${sourceType[resolvedType]?.label}`}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            width={28}
            height={28}
            color={getSourceTypeForegroundColor(bgcolor)}
            bgcolor={bgcolor}
            borderRadius="5px"
          >
            {sourceType[resolvedType]?.icon}
          </Box>
        </Tooltip>
      );
    }

    case 'mailingApproval':
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.primary' }}>
          {value ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : <CloseRoundedIcon sx={{ fontSize: 18 }} />}
        </Box>
      );
    case 'image':
      return value ? (
        <img
          src={value}
          alt="image"
          style={{ width: '70px', objectFit: 'cover', borderRadius: '5px', cursor: 'pointer' }}
          onClick={() => {
            const newWindow = window.open('', '_blank');
            if (newWindow) {
              // Avoid deprecated document.write, use DOM manipulation instead
              newWindow.document.title = 'Image';
              const img = newWindow.document.createElement('img');
              img.src = value as string;
              img.alt = 'image';
              img.style.maxWidth = '100%';
              img.style.maxHeight = '100%';
              img.style.objectFit = 'cover';
              img.style.borderRadius = '5px';
              newWindow.document.body.style.margin = '0';
              newWindow.document.body.style.display = 'flex';
              newWindow.document.body.style.justifyContent = 'center';
              newWindow.document.body.style.alignItems = 'center';
              newWindow.document.body.style.height = '100vh';
              newWindow.document.body.appendChild(img);
            }
          }}
        />
      ) : (
        ''
      );
    default:
      return value || '';
  }
};

const lightSourceBackgrounds = new Set(['primary.light', 'grey.200', 'grey.300']);
export const getSourceTypeForegroundColor = (bgcolor: string) =>
  lightSourceBackgrounds.has(bgcolor) ? 'primary.main' : 'common.white';
export const sourceType: Record<string, { label: string; icon: React.ReactNode; bgcolor: string; percentage: number }> =
  {
    personal_credit: {
      label: 'סליקה אישית',
      icon: <AccountBalanceOutlinedIcon fontSize="small" />,
      bgcolor: 'grey.200',
      percentage: 0
    },
    campaign: {
      label: 'דפים אישיים',
      icon: <SearchOutlinedIcon fontSize="small" />,
      bgcolor: 'error.main',
      percentage: 3
    },
    kiosk: {
      label: 'קיוסק',
      icon: <StoreOutlinedIcon fontSize="small" />,
      bgcolor: 'primary.light',
      percentage: 3
    },
    personal: {
      label: 'לינק אישי',
      icon: <CampaignOutlinedIcon fontSize="small" />,
      bgcolor: 'success.main',
      percentage: 6
    },
    organic: {
      label: 'אורגני',
      icon: <InsertLinkOutlinedIcon fontSize="small" />,
      bgcolor: 'info.main',
      percentage: 10
    },
    paid: {
      label: 'קרן הקמפיינים',
      icon: <CrisisAlertOutlinedIcon fontSize="small" />,
      bgcolor: 'secondary.main',
      percentage: 30
    },
    manual: {
      label: 'ידני',
      icon: <EditNoteOutlinedIcon fontSize="small" />,
      bgcolor: 'grey.200',
      percentage: 0
    },
    dailygive: {
      label: 'תרומה יומית',
      icon: <EventRepeatOutlinedIcon fontSize="small" />,
      bgcolor: 'primary.main',
      percentage: 3
    }
  };
